import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Grid, Box, Typography, Tabs, Tab, Divider } from '@mui/material'
import { Edit, Print, QrCode2, Delete, Add, Download, ReportProblem } from '@mui/icons-material'
import { PageHeader, Button, Card, StatusChip, LoadingSpinner, Modal } from '@components/common'
import { AssetForm, FaultReportForm } from '@components/assets'
import api from '@services/api'
import { formatDate } from '@utils/dateUtils'

const AssetDetail = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [asset, setAsset] = useState(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState(0)
  const [editFormOpen, setEditFormOpen] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [qrModalOpen, setQrModalOpen] = useState(false)
  const [generatingQR, setGeneratingQR] = useState(false)
  const [faultReportFormOpen, setFaultReportFormOpen] = useState(false)

  const fetchAsset = async () => {
    try {
      const response = await api.get(`/assets/assets/${id}/`)
      setAsset(response.data)
    } catch (error) {
      console.error('Error fetching asset:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAsset()
  }, [id])

  const handleDelete = async () => {
    setDeleting(true)
    try {
      await api.delete(`/assets/assets/${id}/`)
      navigate('/assets')
    } catch (error) {
      console.error('Error deleting asset:', error)
    } finally {
      setDeleting(false)
    }
  }

  const handleEditSuccess = () => {
    fetchAsset()
  }

  const handlePrint = () => {
    navigate(`/assets/${id}/print`)
  }

  const handleDownloadQR = () => {
    if (asset.qr_code) {
      const link = document.createElement('a')
      link.href = asset.qr_code
      link.download = `${asset.asset_tag || asset.name}_qr_code.png`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }

  const handleGenerateQR = async () => {
    setGeneratingQR(true)
    try {
      const response = await api.post(`/assets/assets/${id}/generate_qr_code/`)
      setAsset(response.data)
    } catch (error) {
      console.error('Error generating QR code:', error)
    } finally {
      setGeneratingQR(false)
    }
  }

  if (loading) {
    return <LoadingSpinner message="Loading asset details..." />
  }

  if (!asset) {
    return (
      <Box textAlign="center" py={4}>
        <Typography>Asset not found</Typography>
        <Button onClick={() => navigate('/assets')} sx={{ mt: 2 }}>
          Back to Assets
        </Button>
      </Box>
    )
  }

  return (
    <Box>
      <PageHeader
        title={asset.name}
        subtitle={`Asset Tag: ${asset.asset_tag || 'N/A'}`}
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Assets', path: '/assets' },
          { label: asset.name },
        ]}
        actions={
          <>
            <Button
              variant="contained"
              color="error"
              startIcon={<ReportProblem />}
              onClick={() => setFaultReportFormOpen(true)}
            >
              Report Fault
            </Button>
            <Button
              variant="outlined"
              startIcon={<QrCode2 />}
              onClick={() => setQrModalOpen(true)}
            >
              QR Code
            </Button>
            <Button
              variant="outlined"
              startIcon={<Print />}
              onClick={handlePrint}
            >
              Print
            </Button>
            <Button
              startIcon={<Edit />}
              onClick={() => setEditFormOpen(true)}
            >
              Edit
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<Delete />}
              onClick={() => setDeleteModalOpen(true)}
            >
              Delete
            </Button>
          </>
        }
      />

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <Box mb={2}>
              <Tabs
                value={activeTab}
                onChange={(_, newValue) => setActiveTab(newValue)}
              >
                <Tab label="Details" />
                <Tab label="Work Orders" />
                <Tab label="PM Schedule" />
                <Tab label="Documents" />
              </Tabs>
            </Box>
            <Divider sx={{ mb: 3 }} />

            {activeTab === 0 && (
              <Grid container spacing={3}>
                {/* Basic Information */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
                    Basic Information
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    Status
                  </Typography>
                  <Box mt={0.5}>
                    <StatusChip status={asset.status} />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    Criticality
                  </Typography>
                  <Box mt={0.5}>
                    <StatusChip status={asset.criticality} type="criticality" />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    Category
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {asset.category?.name || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    Section/Unit
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {asset.department_name || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    Facility
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {asset.facility_name || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    Number of Units
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {asset.unit_count || 1}
                  </Typography>
                </Grid>

                {/* Technical Details */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" color="primary" sx={{ mb: 1, mt: 2 }}>
                    Technical Details
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    Manufacturer
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {asset.manufacturer?.name || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    Model Number
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {asset.model_number || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    Serial Number
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {asset.serial_number || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    Engraved No / Asset Number
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {asset.engraved_no || '-'}
                  </Typography>
                </Grid>

                {/* Location */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" color="primary" sx={{ mb: 1, mt: 2 }}>
                    Location
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    Location
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {asset.location?.name || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    Room Number
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {asset.room_number || '-'}
                  </Typography>
                </Grid>

                {/* Purchase & Warranty */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" color="primary" sx={{ mb: 1, mt: 2 }}>
                    Purchase & Warranty
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    Purchase Date
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {asset.purchase_date || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    Purchase Cost
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {asset.purchase_cost ? `UGX ${Number(asset.purchase_cost).toLocaleString()}` : '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    Warranty Expiry
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {asset.warranty_expiry || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    Expected Life
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {asset.expected_life_years ? `${asset.expected_life_years} years` : '-'}
                  </Typography>
                </Grid>

                {/* Manufacturing & Origin */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" color="primary" sx={{ mb: 1, mt: 2 }}>
                    Manufacturing & Origin
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    Manufacturing Date
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {asset.manufacturing_date || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    Country of Origin
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {asset.country_of_origin || '-'}
                  </Typography>
                </Grid>

                {/* Receiving Information */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" color="primary" sx={{ mb: 1, mt: 2 }}>
                    Receiving Information
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    Supplier
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {asset.supplier_name || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    Delivered By
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {asset.delivered_by || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    Received By
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {asset.received_by_name || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    Received Date
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {asset.received_date || '-'}
                  </Typography>
                </Grid>

                {/* Remarks */}
                {asset.notes && (
                  <>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="subtitle2" color="primary" sx={{ mb: 1, mt: 2 }}>
                        Remarks
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2">
                        {asset.notes}
                      </Typography>
                    </Grid>
                  </>
                )}
              </Grid>
            )}

            {activeTab === 1 && (
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="subtitle2">Work Orders</Typography>
                  <Button size="small" startIcon={<Add />}>
                    Create Work Order
                  </Button>
                </Box>
                <Typography color="text.secondary">
                  No work orders found for this asset
                </Typography>
              </Box>
            )}

            {activeTab === 2 && (
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="subtitle2">PM Schedule</Typography>
                  <Button size="small" startIcon={<Add />}>
                    Add PM Schedule
                  </Button>
                </Box>
                <Typography color="text.secondary">
                  No preventive maintenance scheduled for this asset
                </Typography>
              </Box>
            )}

            {activeTab === 3 && (
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="subtitle2">Documents</Typography>
                  <Button size="small" startIcon={<Add />}>
                    Upload Document
                  </Button>
                </Box>
                <Typography color="text.secondary">
                  No documents uploaded for this asset
                </Typography>
              </Box>
            )}
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          {/* Asset Images */}
          {(asset.image || asset.image_2 || asset.image_3 || asset.qr_code) && (
            <Card title="Images" sx={{ mb: 3 }}>
              <Box display="flex" flexDirection="column" gap={2}>
                {asset.image && (
                  <Box
                    component="img"
                    src={asset.image}
                    alt={asset.name}
                    sx={{
                      width: '100%',
                      maxHeight: 200,
                      objectFit: 'contain',
                      borderRadius: 1,
                      bgcolor: 'grey.100',
                    }}
                  />
                )}
                <Box display="flex" gap={1} flexWrap="wrap">
                  {asset.image_2 && (
                    <Box
                      component="img"
                      src={asset.image_2}
                      alt={`${asset.name} - 2`}
                      sx={{
                        width: 80,
                        height: 60,
                        objectFit: 'cover',
                        borderRadius: 1,
                        cursor: 'pointer',
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    />
                  )}
                  {asset.image_3 && (
                    <Box
                      component="img"
                      src={asset.image_3}
                      alt={`${asset.name} - 3`}
                      sx={{
                        width: 80,
                        height: 60,
                        objectFit: 'cover',
                        borderRadius: 1,
                        cursor: 'pointer',
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    />
                  )}
                  {asset.qr_code && (
                    <Box
                      component="img"
                      src={asset.qr_code}
                      alt="QR Code"
                      sx={{
                        width: 80,
                        height: 80,
                        objectFit: 'contain',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        bgcolor: 'white',
                      }}
                    />
                  )}
                </Box>
              </Box>
            </Card>
          )}

          <Card title="Quick Info">
            <Box display="flex" flexDirection="column" gap={2}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Asset Tag
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {asset.asset_tag || 'Not assigned'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Created By
                </Typography>
                <Typography variant="body2">
                  {asset.created_by_name || '-'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Created At
                </Typography>
                <Typography variant="body2">
                  {formatDate(asset.created_at)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Last Updated
                </Typography>
                <Typography variant="body2">
                  {formatDate(asset.updated_at)}
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
      </Grid>

      <AssetForm
        open={editFormOpen}
        onClose={() => setEditFormOpen(false)}
        asset={asset}
        onSuccess={handleEditSuccess}
      />

      <FaultReportForm
        open={faultReportFormOpen}
        onClose={() => setFaultReportFormOpen(false)}
        asset={asset}
        onSuccess={() => {
          setFaultReportFormOpen(false)
          // Optionally navigate to fault reports or show success
        }}
      />

      <Modal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Delete Asset"
        maxWidth="xs"
        actions={
          <>
            <Button variant="outlined" onClick={() => setDeleteModalOpen(false)} disabled={deleting}>
              Cancel
            </Button>
            <Button color="error" onClick={handleDelete} loading={deleting}>
              Delete
            </Button>
          </>
        }
      >
        <Typography>
          Are you sure you want to delete "{asset.name}"? This action cannot be undone.
        </Typography>
      </Modal>

      <Modal
        open={qrModalOpen}
        onClose={() => setQrModalOpen(false)}
        title="Asset QR Code"
        maxWidth="xs"
        actions={
          <>
            <Button variant="outlined" onClick={() => setQrModalOpen(false)}>
              Close
            </Button>
            {asset.qr_code ? (
              <Button startIcon={<Download />} onClick={handleDownloadQR}>
                Download
              </Button>
            ) : (
              <Button onClick={handleGenerateQR} loading={generatingQR}>
                Generate QR Code
              </Button>
            )}
          </>
        }
      >
        <Box display="flex" flexDirection="column" alignItems="center" py={2}>
          {asset.qr_code ? (
            <>
              <Box
                component="img"
                src={asset.qr_code}
                alt="Asset QR Code"
                sx={{
                  width: 200,
                  height: 200,
                  objectFit: 'contain',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  bgcolor: 'white',
                  p: 1,
                }}
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {asset.asset_tag || asset.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Scan to view asset details
              </Typography>
            </>
          ) : (
            <Box textAlign="center">
              <QrCode2 sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography color="text.secondary">
                No QR code has been generated for this asset yet.
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Click "Generate QR Code" to create one.
              </Typography>
            </Box>
          )}
        </Box>
      </Modal>
    </Box>
  )
}

export default AssetDetail
